import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { Button } from '../../../components/ui/button.js';
import { Combobox } from '../../../components/ui/combobox.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog.js';
import { Label } from '../../../components/ui/label.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select.js';
import { useScenariosList } from '../hooks.js';
import {
  useScenarioBlueprintDetails,
  useScenarioBlueprints,
  type ScenarioBlueprintVersion,
} from '../hooks.js';
import { asScenarioRow, type ScenarioRow } from '../types.js';

/**
 * Scenario blueprint compare. Mirrors the legacy
 * `wf-audit/src/slices/scenario/components/scenario/CompareCode.tsx` —
 * pick two scenarios + version on each side, fetch the blueprint JSON
 * via the engine proxy (Fusion API v3, license S2S credential), render the
 * two payloads in Monaco's `DiffEditor`. Read-only, license-scoped.
 *
 * Differences from legacy:
 *   • Engine + app-api proxy replaces the legacy SPA's direct Fusion
 *     fetch — the customer's Fusion credential never reaches the
 *     browser.
 *   • Scenario picker reads from the existing license-scoped list
 *     cache (no team selector — the new SPA's list is already
 *     filtered to the operator's license).
 *   • Monaco is dynamically imported so the main bundle stays slim
 *     until the dialog opens for the first time.
 */
const MonacoDiff = lazy(() =>
  import('@monaco-editor/react').then((m) => ({ default: m.DiffEditor })),
);

export interface ScenarioCompareCodeDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Optional default fusionId to seed the left pane (the row the
   *  operator launched the dialog from). */
  defaultFusionId?: string;
}

export function ScenarioCompareCodeDialog({
  open,
  onOpenChange,
  defaultFusionId,
}: ScenarioCompareCodeDialogProps): JSX.Element {
  const { data: rawScenarios = [], isLoading: scenariosLoading } = useScenariosList();
  const scenarios = useMemo<ReadonlyArray<ScenarioRow>>(
    () => rawScenarios.map(asScenarioRow).filter((r) => r.fusionId.length > 0),
    [rawScenarios],
  );

  const [leftScenarioId, setLeftScenarioId] = useState<string>('');
  const [leftVersionId, setLeftVersionId] = useState<string>('');
  const [rightScenarioId, setRightScenarioId] = useState<string>('');
  const [rightVersionId, setRightVersionId] = useState<string>('');
  const [leftJson, setLeftJson] = useState<string>('');
  const [rightJson, setRightJson] = useState<string>('');
  const [isComparing, setIsComparing] = useState(false);
  const [compareError, setCompareError] = useState<string | null>(null);
  // One-shot seed flag — without this the seeding effect re-runs on
  // every leftScenarioId change and snaps the left picker back to
  // the default, blocking the operator from picking anything else.
  const [seeded, setSeeded] = useState(false);

  // Seed on first open. When the dialog re-opens against a different
  // row (operator clicks Compare on another scenario), reset to that
  // row on both sides so version-vs-version is the default workflow.
  useEffect(() => {
    if (!open) {
      setSeeded(false);
      return;
    }
    if (seeded || scenarios.length === 0) return;
    if (defaultFusionId && scenarios.some((s) => s.fusionId === defaultFusionId)) {
      setLeftScenarioId(defaultFusionId);
      setRightScenarioId(defaultFusionId);
    } else {
      const seed = scenarios[0]!.fusionId;
      setLeftScenarioId(seed);
      setRightScenarioId(seed);
    }
    setSeeded(true);
  }, [open, defaultFusionId, scenarios, seeded]);

  const { data: leftVersions = [], isLoading: leftVersionsLoading } = useScenarioBlueprints(
    leftScenarioId || undefined,
  );
  const { data: rightVersions = [], isLoading: rightVersionsLoading } = useScenarioBlueprints(
    rightScenarioId || undefined,
  );

  // Default each side to its newest version once versions load.
  useEffect(() => {
    if (leftVersions.length > 0 && !leftVersions.some((v) => versionKey(v) === leftVersionId)) {
      setLeftVersionId(versionKey(leftVersions[0]!));
    }
  }, [leftVersions, leftVersionId]);
  useEffect(() => {
    if (rightVersions.length > 0 && !rightVersions.some((v) => versionKey(v) === rightVersionId)) {
      setRightVersionId(versionKey(rightVersions[0]!));
    }
  }, [rightVersions, rightVersionId]);

  // Reset rendered JSON when any input changes — keeps the diff
  // honest. The operator clicks Compare to refetch + render.
  useEffect(() => {
    setLeftJson('');
    setRightJson('');
    setCompareError(null);
  }, [leftScenarioId, leftVersionId, rightScenarioId, rightVersionId]);

  const leftVersionIdNum = parseVersionId(leftVersionId);
  const rightVersionIdNum = parseVersionId(rightVersionId);
  const { data: leftBlueprint, error: leftError } = useScenarioBlueprintDetails(
    isComparing ? leftScenarioId || undefined : undefined,
    isComparing ? leftVersionIdNum : undefined,
  );
  const { data: rightBlueprint, error: rightError } = useScenarioBlueprintDetails(
    isComparing ? rightScenarioId || undefined : undefined,
    isComparing ? rightVersionIdNum : undefined,
  );

  // Render the JSON once both blueprints land. Done as an effect so
  // we can also surface a combined error and turn off the spinner.
  useEffect(() => {
    if (!isComparing) return;
    if (leftError || rightError) {
      setCompareError((leftError ?? rightError)!.message);
      setIsComparing(false);
      return;
    }
    if (leftBlueprint !== undefined && rightBlueprint !== undefined) {
      setLeftJson(JSON.stringify(leftBlueprint ?? {}, null, 2));
      setRightJson(JSON.stringify(rightBlueprint ?? {}, null, 2));
      setIsComparing(false);
    }
  }, [isComparing, leftBlueprint, rightBlueprint, leftError, rightError]);

  const canCompare =
    !!leftScenarioId &&
    !!rightScenarioId &&
    leftVersionIdNum !== undefined &&
    rightVersionIdNum !== undefined &&
    !isComparing;

  const handleCompare = (): void => {
    setCompareError(null);
    setLeftJson('');
    setRightJson('');
    setIsComparing(true);
  };

  const scenarioOptions = useMemo(
    () =>
      [...scenarios]
        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
        .map((s) => ({ value: s.fusionId, label: s.name || s.fusionId })),
    [scenarios],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] max-w-[95vw] flex-col overflow-hidden border-foreground/15 bg-[color:var(--surface-overlay)] text-foreground xl:max-w-[88vw]">
        <DialogHeader>
          <DialogTitle>Scenario blueprint compare</DialogTitle>
          <DialogDescription className="text-foreground/60">
            Diff the Fusion blueprint JSON between two scenarios / versions.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 xl:flex-row xl:items-end">
          <ScenarioVersionPicker
            sideLabel="Left"
            scenarioId={leftScenarioId}
            onScenarioChange={setLeftScenarioId}
            versionId={leftVersionId}
            onVersionChange={setLeftVersionId}
            scenarioOptions={scenarioOptions}
            versions={leftVersions}
            versionsLoading={leftVersionsLoading}
            scenariosLoading={scenariosLoading}
          />
          <div className="flex shrink-0 items-center justify-center xl:pb-1">
            <Button
              type="button"
              onClick={handleCompare}
              disabled={!canCompare}
              className="bg-[color:var(--brand-yellow)] text-[color:var(--gradient-end)] hover:opacity-90"
            >
              {isComparing ? 'Comparing…' : 'Compare'}
            </Button>
          </div>
          <ScenarioVersionPicker
            sideLabel="Right"
            scenarioId={rightScenarioId}
            onScenarioChange={setRightScenarioId}
            versionId={rightVersionId}
            onVersionChange={setRightVersionId}
            scenarioOptions={scenarioOptions}
            versions={rightVersions}
            versionsLoading={rightVersionsLoading}
            scenariosLoading={scenariosLoading}
          />
        </div>

        {compareError ? (
          <div className="rounded-md border border-rose-400/30 bg-rose-500/10 p-3 text-sm text-rose-800 dark:text-rose-200">
            Could not load blueprint: {compareError}
          </div>
        ) : null}

        <div className="mt-2 min-h-[55vh] flex-1 overflow-hidden rounded-md border border-foreground/10 bg-black/20">
          {leftJson && rightJson ? (
            <Suspense
              fallback={
                <div className="flex h-full items-center justify-center text-sm text-foreground/40">
                  Loading editor…
                </div>
              }
            >
              <MonacoDiff
                original={leftJson}
                modified={rightJson}
                language="json"
                theme="vs-dark"
                options={{
                  readOnly: true,
                  minimap: { enabled: true },
                  scrollBeyondLastLine: false,
                  fontSize: 12,
                  wordWrap: 'on',
                  automaticLayout: true,
                  renderSideBySide: true,
                  stickyScroll: { enabled: false },
                }}
                height="55vh"
              />
            </Suspense>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-foreground/40">
              {isComparing
                ? 'Fetching blueprint JSON…'
                : 'Pick scenarios + versions and hit Compare to see the diff.'}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface ScenarioVersionPickerProps {
  sideLabel: 'Left' | 'Right';
  scenarioId: string;
  onScenarioChange: (next: string) => void;
  versionId: string;
  onVersionChange: (next: string) => void;
  scenarioOptions: ReadonlyArray<{ value: string; label: string }>;
  versions: ReadonlyArray<ScenarioBlueprintVersion>;
  versionsLoading: boolean;
  scenariosLoading: boolean;
}

function ScenarioVersionPicker({
  sideLabel,
  scenarioId,
  onScenarioChange,
  versionId,
  onVersionChange,
  scenarioOptions,
  versions,
  versionsLoading,
  scenariosLoading,
}: ScenarioVersionPickerProps): JSX.Element {
  return (
    <div className="flex flex-1 flex-col gap-3 rounded-md border border-foreground/10 bg-foreground/[0.02] p-3">
      <span className="text-xs uppercase tracking-wide text-foreground/40">{sideLabel}</span>
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <Label className="text-xs text-foreground/70">Scenario</Label>
          <Combobox
            value={scenarioId}
            onValueChange={onScenarioChange}
            options={[...scenarioOptions]}
            placeholder={scenariosLoading ? 'Loading…' : 'Pick scenario'}
            searchPlaceholder="Search scenarios…"
            emptyText="No scenarios."
          />
        </div>
        <div className="flex w-full flex-col gap-1 sm:w-56">
          <Label className="text-xs text-foreground/70">Version</Label>
          <Select
            value={versionId}
            onValueChange={onVersionChange}
            disabled={versionsLoading || versions.length === 0}
          >
            <SelectTrigger className="border-foreground/15 bg-foreground/5 text-foreground">
              <SelectValue
                placeholder={
                  versionsLoading
                    ? 'Loading…'
                    : versions.length === 0
                      ? 'No versions'
                      : 'Pick version'
                }
              />
            </SelectTrigger>
            <SelectContent>
              {versions.map((v) => (
                <SelectItem key={versionKey(v)} value={versionKey(v)}>
                  {formatVersionLabel(v)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

function versionKey(v: ScenarioBlueprintVersion): string {
  // `id` is preferred — Fusion uses it as the `blueprintId` query
  // param. Falls back to `version` when the engine response only
  // surfaces the version number (older tenants).
  return String(v.id ?? v.version ?? '');
}

function parseVersionId(raw: string): number | undefined {
  if (!raw) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

function formatVersionLabel(v: ScenarioBlueprintVersion): string {
  const versionTag = v.version !== undefined ? `v${v.version}` : `id ${v.id ?? '?'}`;
  if (!v.created) return versionTag;
  const d = new Date(v.created);
  if (Number.isNaN(d.getTime())) return versionTag;
  const date = d.toLocaleDateString(undefined, {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
  const time = d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${versionTag} · ${date} ${time}`;
}
